import React, { Fragment, ReactNode } from 'react';

import t from '../../../labels/labelling-utils';
import { IGenericObject } from '../../../typings/common-types';
import { RoundName } from '../../../typings/match';
import { isValidArray } from '../../../utils/array';
import { getClassificationByUEFACompetitionQualification } from '../../../utils/competition-qualification';
import { getRankingLegendsByQualifications, rankingLegendItems } from '../../../utils/ranking-legends';
import { slugifyCamelCase } from '../../../utils/slugify';
import { makeClassNames } from '../../../utils/styles';
import ErrorComponent from '../../atoms/error/error';
import ResultsTitle from '../../atoms/results-title/results-title';
import Spacer from '../../atoms/spacer/spacer';
import Legend from '../../molecules/legend/legend';
import CompetitionRankingTable, {
  CompetitionHighlight,
  ICompetitionRankingRow,
  ICompetitionRankingTable,
} from '../../organisms/competition-ranking-table/competition-ranking-table';
import ErrorBoundary from '../error-boundary/error-boundary';
import './competition-ranking.scss';

export type RankingVariant = 'teamRankingVariant' | 'competitionRankingVariant';

export const ROUND_UNICO: RoundName = 'unico';
export interface ICompetitionRanking {
  rankingLists: ICompetitionRankingList[];
  team?: string[]; // TEAMS to HIGHLIGHT (competitionRankingVariant)
  teamId?: string; // TEAM to HIGHLIGHT in all COMPETITIONS RANKINGS (teamRankingVariant)
  trackingDataList?: IGenericObject[]; // Passed from WebApp, e.g. ['tracking-serie-a', 'tracking-champions-league'] for each COMPETITION inside rankingLists[]
}

export interface ICompetitionRankingList {
  competitionId: number | string; // FIXME: BE QUERIES return BOTH!
  seasonId: number | string; // FIXME: BE QUERIES return BOTH!
  teams?: ICompetitionRankingRow[];
  customCompetitionName?: string;
  roundName?: RoundName;
}

const TEAM_RANKING_VARIANT: RankingVariant = 'teamRankingVariant';
const COMPETITION_RANKING_VARIANT: RankingVariant = 'competitionRankingVariant';

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

const getQualificationRankingLegendArray = (teams: Array<ICompetitionRankingRow>): CompetitionHighlight[] => [
  ...new Set(teams.map((team) => team.qualification).filter((elm) => Boolean(elm))),
];

/**
 * Transform uefa qualification object to Design System format
 */
export const transformTeamsUefaQualification = (teams: ICompetitionRankingRow[]): ICompetitionRankingRow[] =>
  teams.map((team) => {
    return {
      ...team,
      qualification: team.qualification
        ? getClassificationByUEFACompetitionQualification(team.qualification)
        : undefined,
    };
  });

const isLinearCompetitionByRoundName = (roundName: RoundName | undefined): boolean => roundName === ROUND_UNICO;

const isValidList = (rankingLists: Array<ICompetitionRankingList>) => Boolean(rankingLists && rankingLists.length > 0);

const getTrackingData = (trackingDataList?: Array<IGenericObject>, index: number = 0): IGenericObject | undefined =>
  trackingDataList && isValidArray(trackingDataList) && trackingDataList[index] ? trackingDataList[index] : undefined;

const getCompetitionName = (
  ranking: ICompetitionRankingList,
  isCompetitionRankingVariant: boolean,
  isLinearCompetition: boolean,
  index: number
): ReactNode => {
  const { roundName, customCompetitionName } = ranking;

  if (isCompetitionRankingVariant) {
    return (
      !isLinearCompetition && (
        <Fragment>
          <ResultsTitle title={`${t('competition.ranking.template.round')} ${roundName}`} titleFontSize="14--16" />
          <Spacer size={20} />
        </Fragment>
      )
    );
  } else {
    return (
      <Fragment>
        <ResultsTitle selfUpdate={index === 0} title={customCompetitionName ?? ''} titleFontSize="14--16" />
        <Spacer size={20} />
        {!isLinearCompetition && roundName && (
          <Fragment>
            <ResultsTitle title={`${t('competition.ranking.template.round')} ${roundName}`} titleFontSize="14--16" />
            <Spacer size={20} />
          </Fragment>
        )}
      </Fragment>
    );
  }
};

const getLegendComponent = (teams: ICompetitionRankingRow[], competitionId: number | string) => {
  const uefaQualificationsArray = getQualificationRankingLegendArray(teams);
  const legendsArray = uefaQualificationsArray.map(getClassificationByUEFACompetitionQualification);
  const rankingLegends = getRankingLegendsByQualifications(legendsArray, competitionId);
  return <Legend items={rankingLegends} dataType="qualification" />;
};

const classNames = () =>
  makeClassNames({
    topScorers: 'top-scorers',
  });

const CompetitionRanking = ({
  /* istanbul ignore next: default value */ rankingLists = [],
  team,
  teamId,
  trackingDataList,
}: ICompetitionRanking) => {
  const cn = classNames();
  const variant: RankingVariant = teamId ? TEAM_RANKING_VARIANT : COMPETITION_RANKING_VARIANT;
  const isCompetitionRankingVariant: boolean = variant === COMPETITION_RANKING_VARIANT;

  return (
    <div className={cn.topScorers} data-variant={slugifyCamelCase(variant)}>
      <ErrorBoundary hasError={!isValidList(rankingLists)} errorComponent={getErrorComponent()}>
        {isCompetitionRankingVariant && (
          <ResultsTitle
            title={t('competition.ranking.template.teams')}
            selfUpdate
            titleFontSize="16"
            titlefontWeight="500"
          />
        )}
        {rankingLists.map((ranking: ICompetitionRankingList, index: number) => {
          const { roundName, teams = [] } = ranking;
          const transformedTeams = transformTeamsUefaQualification(teams);
          const isLinearCompetition = isLinearCompetitionByRoundName(roundName);
          const competitionRankingTable: ICompetitionRankingTable = {
            className: 'competition-ranking',
            rows: transformedTeams,
            showLastMatchTrend: true,
            team,
            teamId,
            competitionId: ranking.competitionId,
            trackingData: getTrackingData(trackingDataList, index),
          };

          /* istanbul ignore next: optional chaining not supported */
          return ranking?.teams?.length ? (
            <Fragment key={`${ranking?.seasonId}-${ranking?.competitionId}`}>
              <Spacer size={30} />
              {getCompetitionName(ranking, isCompetitionRankingVariant, isLinearCompetition, index)}
              <CompetitionRankingTable {...competitionRankingTable} />
              {isLinearCompetition ? getLegendComponent(teams, ranking.competitionId) : null}
              <Legend items={rankingLegendItems} dataType="legend" />
            </Fragment>
          ) : null;
        })}
      </ErrorBoundary>
    </div>
  );
};

export default CompetitionRanking;
